import {IconButton, TableContainer, withStyles}                                                                         from "@material-ui/core";
import ArrowDownward                                                                                                    from '@material-ui/icons/ArrowDownward';
import MaterialTable, {Column, Icons}                                                                                   from "material-table";
import React, {Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useState, useMemo, forwardRef}       from 'react';
import styled                                                                                                           from "styled-components";
import {v4 as uuidv4}                                                                                                   from 'uuid';
import _                                                                                                                from "lodash";

import {Asset, Instrument, UserProfile}                        from '../../../../api/api';
import {theme}                                                 from "../../../../bootstrap/Theme";
import {Icon}                                                  from "../../../../components/icon/Icon";
import {useBoolean}                                            from "../../../../hooks/utils/useBoolean";
import {CancelOrderRequest, Order, OrderSide, AllOrderRequest} from '../../../../trade/types';

interface SideCellProps {
  readonly variant: OrderSide
}

const tableIcons: Icons = {
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />)
}

const Side                  = styled.div<SideCellProps>`
  color: ${p => p.variant === "buy" ? theme(p).palette.success.main : theme(p).palette.error.main };
`

const StyledTableContainer  = withStyles({
  root  : {
    maxHeight : 'calc(100% - 2.5rem)'
  }
})(TableContainer);

export interface OrdersWidgetProps {
  assetIdMap                : {[assetID: number]: Asset}
  clientTradableInstruments : Instrument[] | undefined
  instrumentIDToTicker      : {[instrumentID: number]: string}
  marketPriceWebSocket      : MutableRefObject<WebSocket | null>
  orders                    : { [orderID: string]: Order }
  profile                   : UserProfile | undefined
  setOrders?                : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
}

export interface OrderRow {
  initiation_time    : string
  update_time        : string
  expiration_time    : string
  instrument         : string
  instrument_id      : number
  order_id           : string
  initial_quantity   : number
  side               : React.ReactNode 
  limit              : number | "-"
  remaining_quantity : number
  filled_quantity    : number
  filled_price       : number | "-"
  status             : string
}

export const OrdersWidget = (p: OrdersWidgetProps) => {
  const SELECTABLE_ORDER_TYPE: boolean = process.env.REACT_APP_SELECTABLE_ORDER_TYPE === undefined || process.env.REACT_APP_SELECTABLE_ORDER_TYPE.toLowerCase() === "true",
        [ pendingOrdersRequestSent, setPendingOrdersRequestSent, unsetPendingOrdersRequestSent ] = useBoolean(false),
        [ orderTableData, setOrderTableData ]                                                    = useState<OrderRow[]>([]);
     
  const cancelPendingOrder = useCallback((orderID: string) => {
      if (p.profile && p.profile.relatedEntityId && p.marketPriceWebSocket.current && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {  
        const cancelOrderRequestUUID    : string = uuidv4(),
              cancelPendingOrderRequest : CancelOrderRequest = {
                                                "type"        : "cancel_order",
                                                "order_id"    : orderID,
                                                "cancel_id"   : cancelOrderRequestUUID
                                          };
        console.log("Sending request to cancel order " + orderID);
        if(p.setOrders) {
          p.setOrders(previousState => {
            const time_str  = new Date().toISOString().replace("T"," ").replace("Z","");
            return {
              ...previousState,
              [orderID]: {
                ...previousState[orderID],
                status        : "pending_cancel",
                "update_time" : time_str
              }
            }
          });
        }
        p.marketPriceWebSocket.current.send(JSON.stringify(cancelPendingOrderRequest));

      }
    }, [p]);

  const tableHeaders: Column<OrderRow>[] = useMemo(() => {
    const headers = [
      {
        title: "Initiation Time",
        field: "initiation_time",
        filtering: false,
      },
      {
        title: "Update Time",
        field: "update_time",
        filtering: false,
      },
      {
        title: "Expiration Time",
        field: "expiration_time",
        filtering: false,
      },
      {
        title: "Order ID",
        field: "order_id",
        filtering: false,
      },
      {
        title: "Instrument",
        field: "instrument",
        filtering: false,
      },
      {
        title: "Side",
        field: "side",
        filtering: false,
      },
      {
        title: "Limit Price",
        field: "limit",
        filtering: false,
        hidden: !SELECTABLE_ORDER_TYPE
      },
      {
        title: "Fill Price",
        field: "filled_price",
        filtering: false,
      },
      {
        title: "Initial Quantity",
        field: "initial_quantity",
        filtering: false,
      },
      {
        title: "Filled Quantity",
        field: "filled_quantity",
        filtering: false,
      },
      {
        title: "Remaining Quantity",
        field: "remaining_quantity",
        filtering: false,
      },
      {
        title: "Status",
        field: "status",
        filtering: false,
      },
      {
        title: '',
        field: 'cancel',
        render: ((rowData: OrderRow) => rowData.status==='working' && <IconButton size="small"  onClick={() => {cancelPendingOrder(rowData.order_id)}}><Icon icon='faTrash'/></IconButton>)
      },
    ];
    return headers;
  }, []);

  function ordersProp2OrderRow(e: Order): OrderRow {
    return  {
      initiation_time       : e.initiation_time,
      update_time           : e.update_time ,
      expiration_time       : e.expiration_time === "2262-04-11 23:47:16.854775807" || e.expiration_time === "2262-04-11 23:47:16.000000000" || e.expiration_time === undefined ? "-" : e.expiration_time,
      instrument_id         : e.instrument_id,
      instrument            : p.instrumentIDToTicker[e.instrument_id],
      order_id              : e.order_id,
      initial_quantity      : e.initial_quantity,
      side                  : <Side variant={e.side}>{e.side.toLocaleUpperCase()}</Side>, 
      limit                 : (e.limit !== "nan") ? parseFloat(e.limit) : "-", 
      filled_quantity       : e.filled_quantity,
      filled_price          : (e.filled_price && e.filled_price !== "nan" && !isNaN(e.filled_price)) ? e.filled_price : "-",
      status                : e.status,
      remaining_quantity    : e.initial_quantity - e.filled_quantity
    }
  }

  useEffect( () => {
    // Send WebSocket Initialization Message for Pending Order Message Stream
    if (!pendingOrdersRequestSent && p.profile && p.profile.relatedEntityId && p.marketPriceWebSocket.current && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {
      const retrieveAllOrders: AllOrderRequest = {
        type                  : "retrieve_all_orders",
        client_id             : p.profile.relatedEntityId,
        subscribe_to_updates  : true
      }
      console.log("Requesting pending orders and subscribing to updates");
      p.marketPriceWebSocket.current.send(JSON.stringify(retrieveAllOrders));
      setPendingOrdersRequestSent();
    }
    return () => { if (!p.marketPriceWebSocket.current) { console.log("Clearing pending orders state"); unsetPendingOrdersRequestSent(); } }
  }, [p, pendingOrdersRequestSent, setPendingOrdersRequestSent, unsetPendingOrdersRequestSent]);

  useEffect( () => { 
    const orders: OrderRow[] = _.map(Object.values(p.orders), ordersProp2OrderRow)
    setOrderTableData(orders);
  }, [p]);

  return (
    <>
    <StyledTableContainer>
      <MaterialTable<OrderRow>
        icons = {tableIcons}
        options={{
          toolbar: false,
          paging: false,
          search: false,
          sorting: true,
          maxBodyHeight: 300,
          padding: "dense",
          headerStyle: { position: 'sticky', top: 0, backgroundColor: '#F8F8F8' }
        }}
        columns={tableHeaders}
        data={orderTableData}
      /> 
      </StyledTableContainer>
    </>
  );
};
